<template>
  <v-row no-gutters>
    <v-col cols="12">
      <h2 class="mb-2">Collaborator Assigned</h2>
      <p class="text-body-2 grey--text text--darken-1">
        Services and domains shared with your current account email, grouped by assigner.
      </p>
      <v-alert type="info" outlined class="mb-4 text-body-2">
        <div class="font-weight-medium mb-2">To access these services:</div>
        <ol class="pl-5">
          <li class="mb-1">
            Go to
            <a href="https://login.cldy.com/" target="_blank" rel="noopener noreferrer">
              login.cldy.com
            </a>.
          </li>
          <li class="mb-1">
            In the <strong>Collaborator Login</strong> section on the left,
            enter your email address.
          </li>
          <li class="mb-1">Click <strong>Login</strong>.</li>
          <li class="mb-1">Check your email for the one-time single sign-on link.</li>
          <li>Click the link to open your collaborator access.</li>
        </ol>
      </v-alert>
      <v-form @submit.prevent="searchTable">
        <v-row dense align="center" class="mb-3">
          <v-col cols="12" sm="6" md="4">
            <v-text-field v-model="search" label="Search owner, service, or domain"
              prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable
            ></v-text-field>
          </v-col>
          <v-col cols="auto">
            <v-btn type="submit" color="primary" small :disabled="loading">Search</v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn small text :disabled="loading" @click="loadTable">Refresh</v-btn>
          </v-col>
        </v-row>
      </v-form>
      <v-alert v-if="error" type="error" dense>{{ error }}</v-alert>
      <v-data-table :headers="headers" :items="items" item-key="id"
        :options.sync="options" :server-items-length="totalItems" :loading="loading"
        class="elevation-1 assigned-table" :footer-props="{
          'items-per-page-options': [30, 60, 100, 150, 250],
          'items-per-page-text': 'Assigners per page:',
        }"
        :no-data-text="error
          ? 'Assignments could not be loaded.' : 'No services are assigned to you.'"
      >
        <template v-slot:item.assigned_by_email="{ item }">
          <div class="assigned-owner py-3">
            <div class="font-weight-medium">{{ item.assigned_by_email }}</div>
            <div class="text-caption grey--text text--darken-1">
              {{ item.service_count }} assigned {{ item.service_count === 1 ? 'item' : 'items' }}
            </div>
          </div>
        </template>
        <template v-slot:item.services="{ item }">
          <div v-if="item.access_type === 'All services'" class="py-3 font-weight-medium">
            All Services
          </div>
          <div v-else class="assigned-services py-1">
            <div v-for="service in item.services" :key="service.service_id"
              class="assigned-service py-2"
            >
              <div class="assigned-service-details">
                <div class="font-weight-medium">{{ service.service_identity || '—' }}</div>
                <div class="text-caption grey--text text--darken-1">
                  {{ service.service_name }}
                </div>
              </div>
              <v-chip x-small outlined class="ml-3 flex-shrink-0">
                {{ service.status_name }}
              </v-chip>
            </div>
          </div>
        </template>
      </v-data-table>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: 'CollaboratorAssigned',
  data() {
    return {
      search: '',
      appliedSearch: '',
      items: [],
      totalItems: 0,
      loading: false,
      error: '',
      requestId: 0,
      options: {
        page: 1, itemsPerPage: 30, sortBy: ['assigned_by_email'], sortDesc: [false],
      },
      headers: [
        { text: 'Assigned By', value: 'assigned_by_email' },
        { text: 'Services / Domains', value: 'services', sortable: false },
      ],
    };
  },
  watch: {
    options: {
      handler() { this.loadTable(); },
      deep: true,
    },
  },
  beforeDestroy() {
    this.requestId += 1;
  },
  methods: {
    searchTable() {
      this.appliedSearch = (this.search || '').trim();
      if (this.options.page !== 1) {
        this.options.page = 1;
      } else {
        this.loadTable();
      }
    },
    async loadTable() {
      const requestId = this.requestId + 1;
      this.requestId = requestId;
      this.loading = true;
      this.error = '';
      const {
        page, itemsPerPage, sortBy, sortDesc,
      } = this.options;
      try {
        const response = await this.$api.post('customer/collaborator/assigned', {
          search: { '*': this.appliedSearch },
          current_page: page,
          per_page: itemsPerPage,
          order: `${sortBy[0] || 'assigned_by_email'}:${sortDesc[0] === true}`,
        }, { suppressErrorAlert: true });
        if (requestId !== this.requestId) return;
        this.items = response.data.data;
        this.totalItems = response.data.total;
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.items = [];
        this.totalItems = 0;
        this.error = 'Unable to load assigned services. Please try again.';
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.assigned-owner,
.assigned-service-details {
  overflow-wrap: anywhere;
  min-width: 0;
}
.assigned-services {
  width: 100%;
}
.assigned-service {
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
}
.assigned-service + .assigned-service {
  border-top: 1px solid #eeeeee;
}
.assigned-table ::v-deep tbody td {
  vertical-align: top;
}
.assigned-table ::v-deep .v-data-table__wrapper > table > tbody > tr > .v-data-table__mobile-row {
  flex-wrap: wrap;
  gap: 8px;
  height: auto;
  min-height: 48px;
  padding-top: 8px;
  padding-bottom: 8px;
}
.assigned-table ::v-deep .v-data-table__mobile-row__cell {
  flex: 1 1 100%;
  min-width: 0;
  max-width: 100%;
  text-align: left;
}
</style>
